import { Account } from "@common/types/domains/accounting/account";
import { findAccountByStoredName } from "@components/accounts/findAccountByStoredName";
import {
    AccountIdentity,
    ClassifiableAction,
    ResolveAccountIdentity,
    getRuleDirections,
    getRuleEntityIds,
    getSingleAccountAction,
    ruleMatchesCategory,
} from "@common/helpers/rules";
import { RuleActionType, TransactionRule } from "@common/types/transactionRule";

export type CategoryGlMappingDirection = "in" | "out";

export interface EntityDirectionDefaults {
    in: AccountIdentity | null;
    out: AccountIdentity | null;
}

export type DefaultsByEntityId = Map<number, EntityDirectionDefaults>;

export type WorkspaceAccount = Account & { entityId: number };

export function accountToIdentity(account: Account): AccountIdentity {
    return {
        name: account.name,
        type: account.type,
        subtype: account.subtype,
    };
}

function resolveAccountForAction(
    action: ClassifiableAction,
    accounts: WorkspaceAccount[],
): WorkspaceAccount | null {
    if (typeof action.value !== "string") {
        return null;
    }
    if (action.actionType === RuleActionType.ACCOUNT_ID) {
        return accounts.find((account) => account.id === action.value) ?? null;
    }
    if (action.actionType === RuleActionType.ACCOUNT_NAME) {
        return findAccountByStoredName(action.value, accounts) ?? null;
    }
    return null;
}

export function buildAccountResolver(
    accounts: WorkspaceAccount[],
): ResolveAccountIdentity {
    return (action, entityId) => {
        const entityAccounts = accounts.filter(
            (account) => account.entityId === Number(entityId),
        );
        const account = resolveAccountForAction(action, entityAccounts);
        return account ? accountToIdentity(account) : null;
    };
}

interface ResolveEntityAccountIdParams {
    accounts: WorkspaceAccount[];
    entityId: number;
    identity: AccountIdentity;
}

export function resolveEntityAccountIdByIdentity({
    accounts,
    entityId,
    identity,
}: ResolveEntityAccountIdParams): string | null {
    const match = accounts.find(
        (account) =>
            account.entityId === entityId &&
            account.name === identity.name &&
            account.type === identity.type &&
            account.subtype === identity.subtype,
    );
    return match?.id ?? null;
}

export function areIdentitiesEqual(
    a: AccountIdentity | null,
    b: AccountIdentity | null,
): boolean {
    if (a === null || b === null) {
        return a === b;
    }
    return a.name === b.name && a.type === b.type && a.subtype === b.subtype;
}

interface BuildDefaultsParams {
    parentCategoryId: string;
    entityIds: number[];
    defaultRules: TransactionRule[];
    accounts: WorkspaceAccount[];
}

export function buildDefaultsByEntityId({
    parentCategoryId,
    entityIds,
    defaultRules,
    accounts,
}: BuildDefaultsParams): DefaultsByEntityId {
    const resolve = buildAccountResolver(accounts);
    const categoryIdSet = new Set([parentCategoryId]);
    const matchedRules = defaultRules.filter(
        (rule) => !rule.isDisabled && ruleMatchesCategory(rule, categoryIdSet),
    );

    const defaults: DefaultsByEntityId = new Map(
        entityIds.map((entityId) => [entityId, { in: null, out: null }]),
    );

    for (const rule of matchedRules) {
        const action = getSingleAccountAction(rule);
        const directions = getRuleDirections(rule);
        const ruleEntityIds = getRuleEntityIds(rule);
        if (!action || !directions) {
            continue;
        }
        const targetEntityIds = ruleEntityIds ?? entityIds;
        for (const entityId of targetEntityIds) {
            const slot = defaults.get(entityId);
            if (!slot) {
                continue;
            }
            const identity = resolve(action, entityId);
            if (!identity) {
                continue;
            }
            for (const direction of directions) {
                slot[direction] = identity;
            }
        }
    }

    return defaults;
}

interface SharedIdentityParams {
    entityIds: number[];
    direction: CategoryGlMappingDirection;
    defaults: DefaultsByEntityId;
}

export function getSharedDefaultIdentity({
    entityIds,
    direction,
    defaults,
}: SharedIdentityParams): AccountIdentity | null {
    if (entityIds.length === 0) {
        return null;
    }
    const [firstEntityId, ...rest] = entityIds;
    const reference = defaults.get(firstEntityId)?.[direction] ?? null;
    if (!reference) {
        return null;
    }
    const isShared = rest.every((entityId) =>
        areIdentitiesEqual(
            defaults.get(entityId)?.[direction] ?? null,
            reference,
        ),
    );
    return isShared ? reference : null;
}
